import { useState } from "react";
import { newUuid } from "@shared-core/domain/object-ui/command-run";
import { Input } from "../../primitives/input";
import { Label } from "../../primitives/label";
import type {
  CustomWidget,
  CustomWidgetProps,
} from "../../patterns/command-shell/types";

/** What a command's CSV reads as: its rows, or what is wrong with the file as a whole. */
export interface ParsedCsv<Row> {
  rows: Row[];
  errors: string[];
}

export type CsvParser<Row> = (text: string) => ParsedCsv<Row>;

/**
 * The CSV upload widget (brief §9.3): choose a file, read it once with the
 * command's own parser, and hand the shell `{upload_id, rows}`. Matches,
 * skipped rows and confirmations come back as the dry run's choices, checks
 * and confirmations; choosing a match re-checks the matches, never the file.
 * Lifted from *Add people from a file*'s choose stage.
 */
function CsvUpload<Row>({
  parse,
  setValues,
  errors,
  disabled,
}: CustomWidgetProps & { parse: CsvParser<Row> }) {
  const [file, setFile] = useState<{ name: string; count: number } | null>(
    null,
  );
  const [problems, setProblems] = useState<string[]>([]);
  const [isReading, setIsReading] = useState(false);

  const choose = async (chosen: File | undefined) => {
    if (!chosen) return;
    setIsReading(true);
    try {
      const parsed = parse(await chosen.text());
      setProblems(parsed.errors);
      if (parsed.errors.length > 0) {
        setFile(null);
        setValues({ upload_id: undefined, rows: undefined });
        return;
      }
      setFile({ name: chosen.name, count: parsed.rows.length });
      // A new id per file chosen: a retried commit of the same file adds nothing twice.
      setValues({ upload_id: newUuid(), rows: parsed.rows });
    } catch {
      setProblems(["Couldn't read that file. Check it's a CSV and try again."]);
    } finally {
      setIsReading(false);
    }
  };

  return (
    <div className="space-y-2 rounded-md border border-border p-3">
      {file ? (
        <p className="text-sm">
          <span className="font-medium">{file.name}</span> ·{" "}
          {file.count.toLocaleString("en-GB")}{" "}
          {file.count === 1 ? "row" : "rows"}{" "}
          <button
            type="button"
            className="text-primary underline"
            disabled={disabled}
            onClick={() => {
              setFile(null);
              setValues({ upload_id: undefined, rows: undefined });
            }}
          >
            Choose another file
          </button>
        </p>
      ) : (
        <div className="space-y-1">
          <Label htmlFor="csv-upload">CSV file</Label>
          <Input
            id="csv-upload"
            type="file"
            accept=".csv,text/csv"
            disabled={disabled || isReading}
            onChange={(e) => void choose(e.target.files?.[0])}
          />
          <p className="text-xs text-muted-foreground">
            The file is checked once, when it is chosen.
          </p>
        </div>
      )}
      {[...problems, ...errors.map((e) => e.message)].map((message) => (
        <p key={message} role="alert" className="text-sm text-red-600">
          {message}
        </p>
      ))}
    </div>
  );
}

/** A CSV widget for one command, reading its file with that command's parser. */
export function csvUploadWidget<Row>(parse: CsvParser<Row>): CustomWidget {
  return {
    ownedPaths: ["upload_id", "rows"],
    Component: (props) => <CsvUpload {...props} parse={parse} />,
  };
}

export { CsvUpload as CsvUploadWidget };
